import * as React from 'react';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ContentPaste from '@mui/icons-material/ContentPaste';
import RepeatIcon from '@mui/icons-material/Repeat';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Menu, Stack, Divider, Chip } from '@mui/material';

interface CubeEntryRightClickMenuProps {
  onAction: (action: CubeEntryRightClickMenuAction) => void;
  menuPos: { x: number; y: number } | null;
  open: boolean;
  isSingleCell: boolean;
  hasClipboard: boolean;
}

export enum CubeEntryRightClickMenuAction {
  Copy = 'copy',
  Paste = 'paste',
  FillDown = 'fill_down',
  FillRight = 'fill_right',
  RepeatValues = 'repeat_values',
  Clear = 'clear',
  Close = 'close',
}

interface MenuItemConfig {
  action: CubeEntryRightClickMenuAction;
  label: string;
  icon: React.ReactNode;
  shortcut: string;
  description?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  condition?: (props: CubeEntryRightClickMenuProps) => boolean;
  disabled?: (props: CubeEntryRightClickMenuProps) => boolean;
  dividerBefore?: boolean;
}

const MENU_ITEMS: MenuItemConfig[] = [
  {
    action: CubeEntryRightClickMenuAction.Copy,
    label: 'Copy',
    icon: <ContentCopy fontSize='small' sx={{ fontSize: '1rem', color: 'primary.main' }} />,
    shortcut: 'Ctrl+C',
    color: 'primary',
  },
  {
    action: CubeEntryRightClickMenuAction.Paste,
    label: 'Paste',
    icon: <ContentPaste fontSize='small' sx={{ fontSize: '1rem', color: 'secondary.main' }} />,
    shortcut: 'Ctrl+V',
    description: 'Auto-expands to fill selection',
    color: 'secondary',
    disabled: (props) => !props.hasClipboard,
  },
  {
    action: CubeEntryRightClickMenuAction.FillDown,
    label: 'Fill Down',
    icon: <ArrowDownwardIcon fontSize='small' sx={{ fontSize: '1rem', color: 'info.main' }} />,
    shortcut: 'Ctrl+D',
    description: 'Repeat top-row value downward',
    color: 'info',
    condition: (props) => !props.isSingleCell,
    dividerBefore: true,
  },
  {
    action: CubeEntryRightClickMenuAction.FillRight,
    label: 'Fill Right',
    icon: <ArrowForwardIcon fontSize='small' sx={{ fontSize: '1rem', color: 'info.main' }} />,
    shortcut: 'Ctrl+R',
    description: 'Repeat left-column value rightward',
    color: 'info',
    condition: (props) => !props.isSingleCell,
  },
  {
    action: CubeEntryRightClickMenuAction.RepeatValues,
    label: 'Repeat Values',
    icon: <RepeatIcon fontSize='small' sx={{ fontSize: '1rem', color: 'success.main' }} />,
    shortcut: 'Ctrl+Shift+R',
    description: 'Fill N columns from this cell',
    color: 'success',
    condition: (props) => props.isSingleCell,
    dividerBefore: true,
  },
  {
    action: CubeEntryRightClickMenuAction.Clear,
    label: 'Clear',
    icon: <DeleteOutlineIcon fontSize='small' sx={{ fontSize: '1rem', color: 'error.main' }} />,
    shortcut: 'Delete',
    description: 'Set selected cells to 0',
    color: 'error',
    dividerBefore: true,
  },
];

export function CubeEntryRightClickMenu({
  onAction,
  open,
  menuPos,
  isSingleCell,
  hasClipboard,
}: CubeEntryRightClickMenuProps) {
  const menuProps = { onAction, open, menuPos, isSingleCell, hasClipboard };

  const filteredItems = MENU_ITEMS.filter(
    (item) => !item.condition || item.condition(menuProps)
  );

  return (
    <Menu
      open={open}
      onClose={() => onAction(CubeEntryRightClickMenuAction.Close)}
      anchorReference='anchorPosition'
      anchorPosition={menuPos ? { top: menuPos.y, left: menuPos.x } : undefined}
      MenuListProps={{
        dense: false,
        sx: { padding: '8px', width: '290px', maxWidth: '320px' },
      }}
      PaperProps={{
        sx: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          borderRadius: '5px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        },
      }}
    >
      <MenuList sx={{ padding: 0 }}>
        {filteredItems.map((item, index) => (
          <React.Fragment key={item.action}>
            {item.dividerBefore && index > 0 && <Divider sx={{ margin: '4px 16px' }} />}
            <MenuItem
              onClick={() => onAction(item.action)}
              disabled={item.disabled ? item.disabled(menuProps) : false}
              sx={{
                padding: '5px 5px',
                minHeight: '46px',
                borderRadius: '2px',
                margin: '2px 2px',
                transition: 'all 0.2s ease-in-out',
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              <Stack direction='column' spacing={0} sx={{ width: '100%' }}>
                <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
                  <Stack direction='row' spacing={1.5} alignItems='center'>
                    {item.icon}
                    <Typography variant='body2' sx={{ fontWeight: 500, fontSize: '0.8rem', color: 'text.primary' }}>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Chip
                    label={item.shortcut}
                    size='small'
                    variant='outlined'
                    sx={{
                      fontSize: '0.6rem',
                      height: '20px',
                      borderColor: `${item.color}.main`,
                      color: `${item.color}.main`,
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  />
                </Stack>
                {item.description && (
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary', fontSize: '0.65rem', marginTop: '2px', paddingLeft: '28px' }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          </React.Fragment>
        ))}
      </MenuList>
    </Menu>
  );
}
